class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      // 1. קריאה רגילה לשופיפיי
      const shopifyFetch = (window._originalFetch || fetch)(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page&section_id=predictive-search`, 
        { signal: this.abortController.signal }
      ).then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      });

      // 2. קריאה ל-JSON של המאמרים עם Fuse.js
      const articlesUrl = window.theme_urls ? window.theme_urls.articles_index : '/assets/articles-index.json';

      const articlesFetch = fetch(articlesUrl)
        .then(res => res.json())
        .then(articles => {
          if (!articles || articles.length === 0 || typeof Fuse === 'undefined') return [];
          
          const fuse = new Fuse(articles, {
            keys: ['title', 'tags'],
            threshold: 0.3,
            ignoreLocation: true
          });
          
          const results = fuse.search(searchTerm);
          return results.slice(0, 10).map(result => result.item);
        }).catch(err => {
          return [];
        });

      const [shopifyHtml, fuseArticles] = await Promise.all([shopifyFetch, articlesFetch]);

      const resultsMarkup = new DOMParser()
        .parseFromString(shopifyHtml, 'text/html')
        .querySelector('#shopify-section-predictive-search').innerHTML;

      const finalHtml = this.combineAndRenderResults(resultsMarkup, fuseArticles);

      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = finalHtml;
      });
      
      this.renderSearchResults(finalHtml);

    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 20) return;
      this.close();
      throw error;
    }
  }

  combineAndRenderResults(shopifyHtml, fuseArticles) {
    if (fuseArticles.length === 0) return shopifyHtml;

    const parser = new DOMParser();
    const doc = parser.parseFromString(shopifyHtml, 'text/html');
    
    // מנסים למצוא את עטיפת התוצאות של שופיפיי
    let resultsWrapper = doc.querySelector('.predictive-search__results-groups-wrapper');
    
    // אם העטיפה לא קיימת, מייצרים אותה
    if (!resultsWrapper) {
      resultsWrapper = document.createElement('div');
      resultsWrapper.className = 'predictive-search__results-groups-wrapper';
      
      const mainContainer = doc.querySelector('#predictive-search-results') || doc.body;
      if (mainContainer.firstChild) {
        mainContainer.insertBefore(resultsWrapper, mainContainer.firstChild);
      } else {
        mainContainer.appendChild(resultsWrapper);
      }
    }
    
    // מזריקים את המאמרים פנימה
    const articlesHtml = this.renderArticleResults(fuseArticles);
    const articleGroup = document.createElement('div');
    articleGroup.className = 'predictive-search__results-group';
    articleGroup.innerHTML = articlesHtml;
    resultsWrapper.appendChild(articleGroup);
    
    return doc.body.innerHTML;
  }

  renderArticleResults(articles) {
    const regularArticles = articles.filter(article => !article.tags.includes('lock'));
    const lockedArticles = articles.filter(article => article.tags.includes('lock'));
    
    let html = '';

    if (regularArticles.length > 0) {
      html += `
        <h3 class="predictive-search__results-group-label">מאמרים</h3>
        <ul role="list" class="predictive-search__results-list" style="padding:0; margin:0; list-style:none;">
          ${regularArticles.map(article => this.buildArticleItemHtml(article)).join('')}
        </ul>
      `;
    }

    if (lockedArticles.length > 0) {
      html += `
        <h3 class="predictive-search__results-group-label" style="margin-top: 20px;">פרוטוקולים לצפייה</h3>
        <ul role="list" class="predictive-search__results-list" style="padding:0; margin:0; list-style:none;">
          ${lockedArticles.map(article => this.buildArticleItemHtml(article)).join('')}
        </ul>
      `;
    }

    return html;
  }

  buildArticleItemHtml(article) {
    const imageHtml = article.image 
      ? `<img class="predictive-search__image" src="${article.image}" alt="${article.title}" width="90" height="90" style="object-fit:cover;">`
      : `<div class="predictive-search__image" style="background:#f4f4f4; width:90px; height:90px; border-radius:6px;"></div>`;

    return `
      <li id="predictive-search-option-article-${article.handle}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${article.url}" class="predictive-search__item predictive-search__item--link" tabindex="-1">
          ${imageHtml}
          <div class="predictive-search__item-content">
            <span class="predictive-search__item-heading">${article.title}</span>
          </div>
        </a>
      </li>
    `;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return; // 🛡️ הגנה מפני קריסות אם האלמנט חסר

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) { // 🛡️ הגנה מפני קריסות אם האלמנט חסר
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);